import * as React from "react";
import { AlertColor, Box, Card, Chip, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    SessionListConfigState,
    updateSessionSearchParams,
} from "../../app/slices/sessionListConfigSlice";
import { SimpleBreadcrumbs } from "../../components/navigation/simpleBreadcrumbs";
import StandardLayout from "../../layouts/standard";
import { getDateStringAdjusted } from "../../utilities/dateHelpers";
import ActiveSessionsTabPanel from "./activeSessionsTabPanel";
import { UserClaimsService, eGroupIds } from "../../services/currentUserService/currentUserService";
import CclTextOrDateSearchBar from "../../components/common/cclLandingPageSearchBars/cclTextOrDateSearchBar";
import CreateSessionDrawer from "./createSessionsDrawer/createSessionsDrawer";
import SystemAlert from "../home/systemAlert";
import { useSearchParams } from "react-router-dom";
import CclAlertSnackbar from "../../components/common/cclAlertSnackbar";
import CclButton from "../../components/common/cclButtons/cclButton";
import SessionsAdvancedSearchControl from "./sessionsAdvancedSearch/searchControl";
import {
    AdvancedSavedSearchItem,
    KeyValue,
    RegistrationSavedSearchItem,
    SessionSavedSearchItem,
} from "../../services/types/enterpriseCoachingApiTypes";
import { EventDocument } from "../../services/types/search.service.types";
import { AdvancedSearchResults } from "../../services/types/enterpriseParticipantApiTypes";

export const Sessions: React.FC = () => {
    // redux state
    const dispatch = useDispatch();
    const config = useSelector((state: any) => state.sessionListConfig);

    const [searchParams] = useSearchParams();
    const searchParamValue = searchParams.get("searchparam");
    const searchparam = searchParamValue ? decodeURIComponent(searchParamValue) : "";

    // local state
    const [localConfig, setLocalConfig] = useState<SessionListConfigState>({ ...config });

    React.useEffect(() => {
        if (!searchParams || searchParams.toString() === "" || !searchparam) {
            return;
        }
        getSessionByKeyword(searchparam);
    }, [searchParams]);

    const [openCreateSession, setOpenCreateSession] = React.useState<boolean>(false);

    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor | undefined>("info");

    const claimsService = new UserClaimsService();
    const isProgramManager = claimsService.IsUserInGroups([eGroupIds.eClientAdmin]);

    const [openAdvancedSearch, setOpenAdvancedSearch] = useState<boolean>(false);

    const [chips, setChips] = React.useState<KeyValue[]>([]);
    const [preSearchChips, setPreSearchChips] = React.useState<KeyValue[]>([]);
    const [baseLength, setBaseLength] = React.useState<number>(0);
    const [excessLength, setExcessLength] = React.useState<number>(0);
    const [showChips, setShowChips] = React.useState<boolean>(false);

    const [useAdvancedSearch, setUseAdvancedSearch] = React.useState<boolean>(false);

    const getSessionsByDate = async (startDate: Date | null, endDate: Date | null) => {
        if (startDate == null || endDate == null) {
            return;
        }
        const newLocalConfig = {
            StartDate: startDate.toISOString(),
            EndDate: endDate.toISOString(),
            Keyword: "*",
            IsDateSearchActive: true,
        };
        setLocalConfig(newLocalConfig);
        dispatch(updateSessionSearchParams(newLocalConfig));
    };

    const getSessionByKeyword = async (searchString: string) => {
        if (searchString == null || searchString === "" || searchString === "*") {
            return;
        }
        const newLocalConfig = {
            StartDate: getDateStringAdjusted(-2), // api requires some date but it won't be used in search
            EndDate: getDateStringAdjusted(2), // api requires some date but it won't be used in search
            Keyword: searchString.trim(),
            IsDateSearchActive: false,
        };
        setLocalConfig(newLocalConfig);
        dispatch(updateSessionSearchParams(newLocalConfig));
    };

    const getSessions = async (keyword: string, startDate: Date | null, endDate: Date | null) => {
        clearChips();
        if (keyword === "") {
            getSessionsByDate(startDate, endDate);
        } else {
            getSessionByKeyword(keyword);
        }
    };

    const sessionObj = {} as SessionSavedSearchItem;
    const registrationObj = {} as RegistrationSavedSearchItem;

    const [currentSearch, setCurrentSearch] = React.useState<AdvancedSavedSearchItem>({
        session: sessionObj,
        registration: registrationObj,
    });

    const [advancedSearchResults, setAdvancedSearchResults] = React.useState<
        EventDocument[] | undefined
    >(undefined);

    const clearChips = () => {
        setUseAdvancedSearch(false);
        setChips([]);
        setPreSearchChips([]);
        setShowChips(false);
        setBaseLength(0);
        setExcessLength(0);
        const sessionObj = {} as SessionSavedSearchItem;
        const registrationObj = {} as RegistrationSavedSearchItem;
        setCurrentSearch({ session: sessionObj, registration: registrationObj });
    };

    const chipsDisplay = () => {
        if (!chips || chips.length === 0 || !showChips) return;
        let displayChips = chips.slice(0, 4);
        let excessChips = chips.slice(4);

        return (
            <>
                {displayChips.map((x) => {
                    if (!x || !x.label || x.label === "") return;
                    return <Chip key={x.id} label={x.label} sx={{ m: 1 }} />;
                })}
                {excessLength > 0 && (
                    <Tooltip
                        title={
                            <Box>
                                {excessChips.map((x) => {
                                    if (!x || !x.label || x.label === "") return;
                                    return (
                                        <Typography variant="body2" key={x.id} sx={{ m: 1 }}>
                                            {x.label}
                                        </Typography>
                                    );
                                })}
                            </Box>
                        }
                        arrow
                        placement="bottom"
                    >
                        <Typography sx={{ m: 1, alignSelf: "center" }}>+{excessLength}</Typography>
                    </Tooltip>
                )}
            </>
        );
    };

    const breadcrumbs = (
        <SimpleBreadcrumbs>
            <Typography variant="h6" color="text.primary">
                {isProgramManager ? "My Sessions" : "Sessions"}
            </Typography>
        </SimpleBreadcrumbs>
    );

    const updateCurrentSearch = (search: AdvancedSavedSearchItem) => {
        setUseAdvancedSearch(true);
        setCurrentSearch(search);
        let temp: any = { ...search.registration, ...search.session };
        temp = Object.values(temp);
        temp = temp.filter(Boolean);
        setPreSearchChips(temp);
        if (
            Object.keys(search.registration).length === 0 &&
            Object.keys(search.session).length === 0
        ) {
            clearChips();
            setAdvancedSearchResults(undefined);
        }
    };

    const onAdvancedSearch = (sessions: AdvancedSearchResults) => {
        setUseAdvancedSearch(true);
        let temp = preSearchChips;
        for (let i = 0; i < temp.length; i++) {
            if (!temp[i]) {
                temp.splice(i, 1);
                i--;
                continue;
            }
            if (temp[i].label === "" || temp[i].label === null || temp[i].label === undefined) {
                temp.splice(i, 1);
                i--;
            }
        }
        setChips(temp);
        if (temp.length > 4) {
            setExcessLength(temp.length - 4);
        } else {
            setExcessLength(0);
        }
        setBaseLength(temp.length);
        setShowChips(true);
        setAdvancedSearchResults(sessions.sessions);
        console.log(sessions);
    };

    return (
        <StandardLayout breadcrumbs={breadcrumbs}>
            <Stack height={1} width={1}>
                {openAdvancedSearch && (
                    <SessionsAdvancedSearchControl
                        onSearchResults={onAdvancedSearch}
                        updateCurrentSearch={updateCurrentSearch}
                        pastSearch={currentSearch}
                        open={openAdvancedSearch}
                        onClose={() => setOpenAdvancedSearch(false)}
                    />
                )}
                {isProgramManager ? (
                    <>
                        <Box
                            sx={{
                                width: "100%",
                                border: "1px solid #E0E0E0",
                                borderRadius: "4px",
                                overflow: "hidden",
                                display: "block",
                                position: "relative",
                            }}
                        >
                            <Box
                                component="img"
                                src="https://coreprodeuscclstg.blob.core.windows.net/public-web-assets/access-assets/images/HomePageTitleBar.jpg"
                                alt="Session List Title Bar"
                                sx={{
                                    width: "100%",
                                    height: "auto",
                                    display: "block",
                                }}
                            />
                        </Box>
                        <Card
                            sx={{
                                height: 1,
                                width: 1,
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <Box sx={{ width: "100%", mt: 1, mb: 2 }}>
                                <SystemAlert
                                    tag="ClientAdminSystemAlert"
                                    title="Client Admin System Alert"
                                />
                            </Box>
                            <ActiveSessionsTabPanel
                                isProgramManager={true}
                                openSidePanel={() => setOpenCreateSession(true)}
                                advancedSearchResults={advancedSearchResults}
                            />
                        </Card>
                    </>
                ) : (
                    <Card
                        sx={{
                            width: 1,
                            height: 1,
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <Box sx={{ display: "flex", flexDirection: "row", width: 1, m: 1 }}>
                            <Box sx={{ flex: "1", paddingRight: 1 }}>
                                <CclTextOrDateSearchBar
                                    executeSearch={getSessions}
                                    initialTextFieldSearchTerm={
                                        searchparam
                                            ? (searchparam ?? "")
                                            : localConfig.IsDateSearchActive
                                              ? ""
                                              : localConfig.Keyword
                                    }
                                    textFieldSearchLabel="Enter PR Code, ID, or Title Keyword"
                                    initialStartDate={
                                        localConfig.IsDateSearchActive
                                            ? new Date(localConfig.StartDate)
                                            : null
                                    }
                                    initialEndDate={
                                        localConfig.IsDateSearchActive
                                            ? new Date(localConfig.EndDate)
                                            : null
                                    }
                                    startDateLabel="Earliest Start Date"
                                    endDateLabel="Latest Start Date"
                                    allowNullDates={false}
                                    showResetButton={true}
                                    clearSearchTrigger={undefined}
                                />
                            </Box>
                        </Box>
                        {claimsService.IsUserCclAdminOrSuperAdmin() && (
                            <>
                                <Divider />
                                <Box
                                    sx={{
                                        width: "100%",
                                        m: 3,
                                        display: "flex",
                                        flexDirection: "row",
                                    }}
                                >
                                    <CclButton
                                        onClick={() => {
                                            setOpenAdvancedSearch(true);
                                        }}
                                        mode={"secondary"}
                                        sx={{ mr: 3 }}
                                    >
                                        <Stack direction={"row"}>
                                            Advanced Search
                                            {baseLength > 0 && showChips && (
                                                <Box
                                                    sx={{
                                                        ml: 2,
                                                        background: "#2F5F95",
                                                        borderRadius: "50%",
                                                        width: "20px",
                                                        color: "white",
                                                    }}
                                                >
                                                    {baseLength}
                                                </Box>
                                            )}
                                        </Stack>
                                    </CclButton>
                                    {chipsDisplay()}
                                </Box>
                                <Divider sx={{ mb: 4 }} />
                            </>
                        )}
                        <Box
                            sx={{
                                width: 1,
                                height: 1,
                                marginTop: 2,
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <ActiveSessionsTabPanel
                                isProgramManager={false}
                                openSidePanel={() => setOpenCreateSession(true)}
                                advancedSearchResults={advancedSearchResults}
                                useAdvancedSearch={useAdvancedSearch}
                            />
                        </Box>
                        {/* </Grid> */}
                    </Card>
                )}

                {openCreateSession && (
                    <CreateSessionDrawer
                        open={openCreateSession}
                        onClose={() => setOpenCreateSession(false)}
                        showSnackbar={function (message: string, severity: AlertColor): void {
                            setShowSnackbar(true);
                            setSnackbarMessage(message);
                            setSnackbarSeverity(severity);
                        }}
                    />
                )}

                {showSnackbar ? (
                    <CclAlertSnackbar
                        open={true}
                        onClose={() => setShowSnackbar(false)}
                        message={snackbarMessage}
                        severity={snackbarSeverity}
                    />
                ) : null}
            </Stack>
        </StandardLayout>
    );
};

export default Sessions;
